import { useCallback, useLayoutEffect, useRef, type RefObject } from "react";

import type { MediaPlayerInstance } from "@vidstack/react";

/** Keep only the latest requested seek, and discard it when its source changes. */
export function useSeekWhenReady(
  playerRef: RefObject<MediaPlayerInstance | null>,
  source: string,
  seek: (time: number) => void,
) {
  const pending = useRef<{ unsubscribe?: () => void } | null>(null);
  const cancel = useCallback(() => {
    const request = pending.current;
    pending.current = null;
    request?.unsubscribe?.();
  }, []);

  // Layout cleanup invalidates the previous request before the new source can
  // publish readiness. The identity guard also rejects already-queued callbacks.
  useLayoutEffect(() => cancel, [source, cancel]);

  return useCallback(
    (time: number) => {
      cancel();
      const player = playerRef.current;
      if (!player) return;
      if (player.state.canPlay) {
        seek(time);
        return;
      }

      const request: { unsubscribe?: () => void } = {};
      pending.current = request;
      request.unsubscribe = player.subscribe((state) => {
        if (pending.current !== request || !state.canPlay) return;
        pending.current = null;
        request.unsubscribe?.();
        seek(time);
      });
      // subscribe may synchronously deliver readiness before returning cleanup.
      if (pending.current !== request) request.unsubscribe();
    },
    [cancel, playerRef, seek],
  );
}
